import { FC } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle,
  XCircle,
  Calculator,
  DollarSign,
  Package,
  Truck,
  Gift,
  TrendingDown,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import { ConfigurationTypeEnum, DiscountTypeEnum } from '@libs/schemas';

interface PricingValidationProps {
  testResult: {
    appliedRules: Array<{
      ruleId: string;
      ruleName: string;
      configuration: {
        type: string;
        data: unknown;
        appliedValue: unknown;
      };
      scope: unknown;
      precedence: number;
      canCombine: boolean;
      appliedReason: string;
    }>;
    pricing: {
      containers: Array<{
        containerId: string;
        quantity: number;
        totalDays: number;
        basePriceInCents: number;
        adjustedPriceInCents: number;
        pricingModel: string;
        appliedRuleId?: string;
      }>;
      materials: {
        totalPriceInCents: number;
        weightInTons: number;
        effectiveWeightInTons: number;
        perTonRateInCents: number;
        minimumChargeInCents: number;
        pricingModel: string;
        appliedRuleId?: string;
        breakdown: Array<{
          materialId: string;
          quantity: number;
          priceInCents: number;
        }>;
      };
      addOns: Array<{
        addOnId: string;
        basePriceInCents: number;
        adjustedPriceInCents: number;
        appliedRuleId?: string;
      }>;
      discounts: Array<{
        type: string;
        percentage: number;
        amountInCents: number;
        appliedRuleId: string;
      }>;
      totals: {
        subtotalInCents: number;
        totalDiscountInCents: number;
        finalTotalInCents: number;
        breakdown: {
          containers: number;
          materials: number;
          addOns: number;
          discounts: number;
        };
      };
    };
    context: {
      order: {
        id: string;
        friendlyId: string;
        status: string;
        userId: string;
      };
      customer: {
        id: string;
        name: string;
        type: string;
        company?: string;
      };
      orderData: {
        totalDays: number;
        calculatedWeightInTons: number;
        containersCount: number;
        materialsCount: number;
        addOnsCount: number;
      };
    };
    metadata: {
      evaluationDate: string;
      ruleResolutionLog?: Array<{
        step: string;
        message: string;
        ruleIds: string[];
        details: unknown;
      }>;
      warnings: Array<{
        code: string;
        message: string;
        severity: string;
        context?: unknown;
      }>;
    };
  };
  testedRules: Array<{
    originalInput?: unknown;
    transformedForEvaluation?: unknown;
  }>;
}

export const PricingValidationSummary: FC<PricingValidationProps> = ({
  testResult,
  testedRules,
}) => {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Helper functions to get names from debug data
  const getMaterialName = (materialId: string): string => {
    const debugData = (testResult as Record<string, unknown>).debugData as Record<string, unknown>;
    const materials = (debugData?.materials as Array<Record<string, unknown>>) || [];
    const material = materials.find((m: Record<string, unknown>) => m._id === materialId);
    return (material?.name as string) || `Material ${materialId.slice(-4)}`;
  };

  const getContainerName = (containerId: string): string => {
    const debugData = (testResult as Record<string, unknown>).debugData as Record<string, unknown>;
    const containers = (debugData?.containers as Array<Record<string, unknown>>) || [];
    const container = containers.find((c: Record<string, unknown>) => c._id === containerId);
    return (container?.name as string) || `Container ${containerId.slice(-4)}`;
  };

  const getCalculationSteps = () => {
    const steps = [];

    // Container calculations
    testResult.pricing.containers.forEach((container, idx) => {
      const isRuleApplied = container.appliedRuleId;
      const basePrice = container.basePriceInCents;
      const finalPrice = container.adjustedPriceInCents;

      steps.push({
        type: 'container',
        label: `Container ${idx + 1}`,
        calculation: isRuleApplied
          ? `Rule Applied: ${formatCurrency(finalPrice)}`
          : `${container.quantity} × ${container.totalDays} days = ${formatCurrency(basePrice)}`,
        amount: finalPrice,
        ruleApplied: isRuleApplied,
        model: container.pricingModel,
      });
    });

    // Materials calculation
    const materials = testResult.pricing.materials;
    steps.push({
      type: 'material',
      label: 'Materials',
      calculation: `${materials.effectiveWeightInTons}t × ${formatCurrency(materials.perTonRateInCents)}`,
      amount: materials.totalPriceInCents,
      ruleApplied: materials.appliedRuleId,
      model: materials.pricingModel,
    });

    // Add-ons
    testResult.pricing.addOns.forEach((addon, idx) => {
      steps.push({
        type: 'addon',
        label: `Add-on ${idx + 1}`,
        calculation: formatCurrency(addon.adjustedPriceInCents),
        amount: addon.adjustedPriceInCents,
        ruleApplied: addon.appliedRuleId,
      });
    });

    // Discounts
    testResult.pricing.discounts.forEach((discount) => {
      steps.push({
        type: 'discount',
        label: `${discount.type.replace('_', ' ')}`,
        calculation:
          discount.percentage > 0
            ? `${discount.percentage.toFixed(1)}% off`
            : 'Flat discount',
        amount: -discount.amountInCents,
        ruleApplied: discount.appliedRuleId,
      });
    });

    return steps;
  };

  const calculationSteps = getCalculationSteps();
  const appliedRuleIds = new Set(testResult.appliedRules.map((r) => r.ruleId));

  // Helper function to determine why a rule didn't match
  const getRuleMismatchReason = (rule: Record<string, unknown>): string => {
    if (!rule.active) {
      return 'Rule is inactive';
    }

    // Check scope mismatches
    const orderContext = testResult.context;
    const scope = (rule.scope as Record<string, unknown>) || {};

    // Customer classification mismatch
    const customerClassification = scope.customerClassification as string;
    if (
      customerClassification &&
      customerClassification !== 'all' &&
      customerClassification !== orderContext.customer.type
    ) {
      return `Customer type mismatch: rule targets ${customerClassification}, order is ${orderContext.customer.type}`;
    }

    // ZIP code mismatch (if we had ZIP data)
    const zipCodes = scope.zipCodes;
    if (zipCodes && Array.isArray(zipCodes) && zipCodes.length > 0) {
      return `ZIP code restriction: rule limited to specific ZIP codes`;
    }

    // Date range mismatch
    const evaluationDate = new Date(testResult.metadata.evaluationDate);
    const effectiveFrom = rule.effectiveFrom as string;
    const effectiveTo = rule.effectiveTo as string;
    if (effectiveFrom && new Date(effectiveFrom) > evaluationDate) {
      return `Not yet effective: starts ${new Date(effectiveFrom).toLocaleDateString()}`;
    }
    if (effectiveTo && new Date(effectiveTo) < evaluationDate) {
      return `Expired: ended ${new Date(effectiveTo).toLocaleDateString()}`;
    }

    // Check rule resolution log for more specific reasons (if available)
    const resolutionLog = testResult.metadata.ruleResolutionLog || [];
    const ruleId = (rule._id || rule.id) as string;
    const logEntry = resolutionLog.find((log) => log.ruleIds && log.ruleIds.includes(ruleId));

    if (logEntry) {
      if (logEntry.step === 'SOW-enforcement') {
        return 'SOW exclusion: discounts are mutually exclusive from pricing rules';
      }
      if (logEntry.step === 'precedence-conflict') {
        return 'Precedence conflict: lower priority than applied rules';
      }
      if (logEntry.step === 'compatibility-check') {
        return 'Configuration incompatibility with other applied rules';
      }
    }

    return 'Matched criteria but not applied';
  };

  // Categorize tested rules
  const ruleEvaluation = testedRules.map((ruleData) => {
    const rule = (ruleData.transformedForEvaluation ||
      ruleData.originalInput ||
      ruleData) as Record<string, unknown>;
    const ruleId = (rule._id || rule.id) as string;
    const wasApplied = appliedRuleIds.has(ruleId);
    const mismatchReason = wasApplied ? null : getRuleMismatchReason(rule);
    return { rule, wasApplied, mismatchReason };
  });

  const appliedRules = ruleEvaluation.filter((r) => r.wasApplied);
  const rejectedRules = ruleEvaluation.filter((r) => !r.wasApplied);

  return (
    <div className="space-y-4">
      {/* Quick Context Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-sm font-medium text-blue-700">
                Order #{testResult.context.order.friendlyId}
              </div>
              <div className="text-xs text-blue-600">
                {testResult.context.customer.name} • {testResult.context.customer.type}
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-blue-700">
              <span>{testResult.context.orderData.calculatedWeightInTons} tons</span>
              <span>{testResult.context.orderData.totalDays} days</span>
              <span>{testResult.context.orderData.containersCount} containers</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-900">
              {formatCurrency(testResult.pricing.totals.finalTotalInCents)}
            </div>
            <div className="text-xs text-blue-600">
              {testResult.appliedRules.length} rules applied
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rules Flow - Visual evaluation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Eye className="h-4 w-4" />
              Rule Evaluation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Applied Rules (affected pricing) */}
            {appliedRules.map(({ rule }, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs"
              >
                <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{rule.name as string}</div>
                  <div className="text-green-600">
                    Applied • P:{rule.precedence as number} •{' '}
                    {((rule.scope as Record<string, unknown>)?.customerClassification as string) ||
                      'Global'}
                  </div>
                </div>
              </div>
            ))}

            {/* Non-Applied Rules */}
            {rejectedRules?.map(({ rule, mismatchReason }, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <XCircle className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{rule.name as string}</div>
                  <div className="text-gray-500">P:{rule.precedence as number}</div>
                  <div className="text-orange-600 text-xs mt-1 leading-tight">{mismatchReason}</div>
                </div>
              </div>
            ))}

            {/* Rule count summary */}
            <div className="pt-2 border-t text-xs text-gray-600">
              {appliedRules.length} applied (affected price), {rejectedRules.length} not applied
            </div>
          </CardContent>
        </Card>

        {/* Calculation Steps - Math validation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Calculator className="h-4 w-4" />
              Calculation Steps
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {calculationSteps.map((step, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  {step.type === 'container' && <Truck className="h-3 w-3 text-blue-500" />}
                  {step.type === 'material' && <Package className="h-3 w-3 text-green-500" />}
                  {step.type === 'addon' && <Gift className="h-3 w-3 text-purple-500" />}
                  {step.type === 'discount' && <TrendingDown className="h-3 w-3 text-red-500" />}
                  <div>
                    <div className="font-medium">{step.label}</div>
                    <div className="text-gray-500">{step.calculation}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">{formatCurrency(Math.abs(step.amount))}</div>
                  {step.ruleApplied && (
                    <Badge variant="outline" className="text-xs h-4">
                      {step.model || 'rule'}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Totals Verification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4" />
              Total Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span>Containers</span>
                <span>{formatCurrency(testResult.pricing.totals.breakdown.containers)}</span>
              </div>
              <div className="flex justify-between">
                <span>Materials</span>
                <span>{formatCurrency(testResult.pricing.totals.breakdown.materials)}</span>
              </div>
              <div className="flex justify-between">
                <span>Add-ons</span>
                <span>{formatCurrency(testResult.pricing.totals.breakdown.addOns)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Subtotal</span>
                <span>{formatCurrency(testResult.pricing.totals.subtotalInCents)}</span>
              </div>
              {testResult.pricing.totals.totalDiscountInCents > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discounts</span>
                  <span>-{formatCurrency(testResult.pricing.totals.totalDiscountInCents)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-bold text-green-600">
                <span>Final Total</span>
                <span>{formatCurrency(testResult.pricing.totals.finalTotalInCents)}</span>
              </div>
            </div>

            {/* Quick validation checks */}
            <div className="pt-2 border-t space-y-1">
              {testResult.metadata.warnings.length > 0 ? (
                <div className="flex items-center gap-1 text-xs text-orange-600">
                  <AlertTriangle className="h-3 w-3" />
                  <span>{testResult.metadata.warnings.length} warnings</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  <span>No warnings</span>
                </div>
              )}

              {/* Calculation sanity check */}
              {(() => {
                const calculatedSubtotal =
                  testResult.pricing.totals.breakdown.containers +
                  testResult.pricing.totals.breakdown.materials +
                  testResult.pricing.totals.breakdown.addOns;
                const reportedSubtotal = testResult.pricing.totals.subtotalInCents;
                const isSubtotalCorrect = Math.abs(calculatedSubtotal - reportedSubtotal) < 1;

                return isSubtotalCorrect ? (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Math checks out</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <XCircle className="h-3 w-3" />
                    <span>Math error detected</span>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Rule Impact - Only show applied rules with their specific impact */}
      {testResult.appliedRules.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4" />
              Applied Rules (Price Impact)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {testResult.appliedRules.map((rule, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg p-3 bg-gradient-to-br from-blue-50 to-indigo-50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-sm">{rule.ruleName}</div>
                      <div className="text-xs text-blue-600">
                        Reason: {rule.appliedReason.replace(/[-_]/g, ' ')} • Precedence:{' '}
                        {rule.precedence}
                      </div>
                    </div>
                    <Badge variant={rule.canCombine ? 'default' : 'secondary'} className="text-xs">
                      {rule.canCombine ? '✓' : '⚠'}
                    </Badge>
                  </div>

                  {(() => {
                    const config = rule.configuration;
                    return (
                      <div className="text-xs space-y-2 border-t pt-2 first:border-t-0 first:pt-0">
                        <div className="font-medium text-blue-800">
                          {config.type === ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX
                            ? 'Container + Material Matrix'
                            : config.type === ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING
                              ? 'Container Period Pricing'
                              : config.type === ConfigurationTypeEnum.TONNAGE_PRICING
                                ? 'Tonnage-Based Pricing'
                                : config.type === ConfigurationTypeEnum.AUTO_DISCOUNT
                                  ? 'Automatic Discount'
                                  : config.type === ConfigurationTypeEnum.DISCOUNT
                                    ? 'Discount'
                                    : config.type.replace(/_/g, ' ')}
                        </div>

                        {/* Show detailed configuration that was applied */}
                        {config.type === ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX && (
                          <div className="space-y-1">
                            <div className="text-gray-600">
                              <span className="font-medium">Pricing Model:</span> Fixed pricing
                            </div>
                            {(config.data as Record<string, unknown>)?.matrixData ? (
                              <div className="text-gray-600">
                                <span className="font-medium">Matrix Configuration:</span>
                                <div className="ml-2 mt-1 space-y-1">
                                  {Object.entries(
                                    (config.data as Record<string, unknown>).matrixData as Record<
                                      string,
                                      unknown
                                    >,
                                  )
                                    .map(([materialId, materialData]) =>
                                      Object.entries(
                                        (materialData as Record<string, unknown>) || {},
                                      )
                                        .filter(
                                          ([, combData]) =>
                                            (combData as Record<string, unknown>)?.enabled,
                                        )
                                        .map(([containerId, combData]) => (
                                          <div
                                            key={`${materialId}-${containerId}`}
                                            className="text-xs bg-blue-50 p-1 rounded"
                                          >
                                            <span className="font-medium">
                                              {getMaterialName(materialId)}
                                            </span>{' '}
                                            +{' '}
                                            <span className="font-medium">
                                              {getContainerName(containerId)}
                                            </span>
                                            :{' '}
                                            {(combData as Record<string, unknown>)
                                              ?.fixedPriceInCents
                                              ? formatCurrency(
                                                  (combData as Record<string, unknown>)
                                                    .fixedPriceInCents as number,
                                                )
                                              : (combData as Record<string, unknown>)
                                                    ?.perTonRateInCents
                                                ? `${formatCurrency((combData as Record<string, unknown>).perTonRateInCents as number)}/ton`
                                                : 'No price set'}
                                          </div>
                                        )),
                                    )
                                    .flat()}
                                </div>
                              </div>
                            ) : null}
                            {/* Show specific applied value if available */}
                            {config.appliedValue &&
                            (config.data as Record<string, unknown>)?.matrixData ? (
                              <div className="bg-green-100 p-1 rounded text-green-800">
                                <span className="font-medium">Applied To:</span> Container pricing
                                (fixed rate per container-material combination)
                              </div>
                            ) : null}
                          </div>
                        )}

                        {config.type === ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING && (
                          <div className="space-y-1">
                            <div className="text-gray-600">
                              <span className="font-medium">Period Pricing:</span> Base prices with
                              included days/tonnage + overage rates
                            </div>
                            {(config.data as Record<string, unknown>)?.containerPeriods ? (
                              <div className="text-gray-600">
                                <span className="font-medium">Container Periods:</span>
                                <div className="ml-2 mt-1 space-y-1">
                                  {Object.entries(
                                    (config.data as Record<string, unknown>)
                                      .containerPeriods as Record<string, unknown>,
                                  ).map(([containerId, periodData]) => (
                                    <div
                                      key={containerId}
                                      className="text-xs bg-purple-50 p-1 rounded"
                                    >
                                      <span className="font-medium">
                                        {getContainerName(containerId)}
                                      </span>
                                      :{' '}
                                      {formatCurrency(
                                        ((periodData as Record<string, unknown>)
                                          ?.basePriceInCents as number) || 0,
                                      )}{' '}
                                      base (
                                      {((periodData as Record<string, unknown>)
                                        ?.includedDays as number) || 0}{' '}
                                      days,{' '}
                                      {((periodData as Record<string, unknown>)
                                        ?.includedTonnage as number) || 0}{' '}
                                      tons included)
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {config.appliedValue ? (
                              <div className="bg-purple-100 p-1 rounded text-purple-800">
                                <span className="font-medium">Applied To:</span> Container pricing
                                with period-based rates and overage calculations
                              </div>
                            ) : null}
                          </div>
                        )}

                        {config.type === ConfigurationTypeEnum.TONNAGE_PRICING && (
                          <div className="space-y-1">
                            <div className="text-gray-600">
                              <span className="font-medium">Rate:</span>{' '}
                              {formatCurrency(
                                ((config.data as Record<string, unknown>)
                                  ?.perTonRateInCents as number) || 0,
                              )}
                              /ton
                            </div>
                            <div className="text-gray-600">
                              <span className="font-medium">Minimum:</span>{' '}
                              {((config.data as Record<string, unknown>)
                                ?.minimumTonnage as number) || 0}{' '}
                              tons
                            </div>
                            {/* Show calculation */}
                            {config.appliedValue ? (
                              <div className="bg-green-100 p-1 rounded text-green-800">
                                <span className="font-medium">Applied To:</span> Material weight
                                pricing - {testResult.context.orderData.calculatedWeightInTons} tons
                                ×{' '}
                                {formatCurrency(
                                  ((config.data as Record<string, unknown>)
                                    ?.perTonRateInCents as number) || 0,
                                )}
                                /ton ={' '}
                                {formatCurrency(
                                  ((config.appliedValue as Record<string, unknown>)
                                    ?.totalPriceInCents as number) || 0,
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}

                        {(config.type === ConfigurationTypeEnum.AUTO_DISCOUNT ||
                          config.type === ConfigurationTypeEnum.DISCOUNT) && (
                          <div className="space-y-1">
                            <div className="text-gray-600">
                              <span className="font-medium">Type:</span>{' '}
                              {((config.data as Record<string, unknown>)
                                ?.discountType as DiscountTypeEnum) || DiscountTypeEnum.PERCENTAGE}
                            </div>
                            {(config.data as Record<string, unknown>)?.discountPercent ? (
                              <div className="text-gray-600">
                                <span className="font-medium">Rate:</span>{' '}
                                {(
                                  ((config.data as Record<string, unknown>)
                                    .discountPercent as number) * 100
                                ).toFixed(1)}
                                % off
                              </div>
                            ) : null}
                            {(config.data as Record<string, unknown>)?.discountAmountInCents ? (
                              <div className="text-gray-600">
                                <span className="font-medium">Amount:</span>{' '}
                                {formatCurrency(
                                  (config.data as Record<string, unknown>)
                                    .discountAmountInCents as number,
                                )}{' '}
                                off
                              </div>
                            ) : null}
                            {/* Show calculation */}
                            {config.appliedValue ? (
                              <div className="bg-red-100 p-1 rounded text-red-800">
                                <span className="font-medium">Applied To:</span> Order total - saved{' '}
                                {formatCurrency(
                                  ((config.appliedValue as Record<string, unknown>)
                                    ?.discountInCents as number) || 0,
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
